import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { Helmet } from 'react-helmet';
import { useRootStore } from '../../../stores/rootStore';

export const EnvironmentDetailsPage = () => {
  const country = useRootStore(state => state.country);

  return (
    <>
      <Helmet>
        <title>
          Guides | Environment details | Cotality Developer Portal |{' '}
          {country.toUpperCase()}
        </title>
      </Helmet>

      <Box flex={1} bgcolor="white">
        <Box maxWidth={1440} m="auto" px={4} pt={4} pb={8}>
          <Typography
            variant="h3"
            fontFamily="TWK Everett"
            fontWeight={600}
            my={0.5}
          >
            Environment details
          </Typography>

          <Typography fontWeight={500}>
            Learn about our three different environments.
          </Typography>

          <br />
          <br />

          <Typography variant="h5" fontWeight={600} my={0.5}>
            Sandbox
          </Typography>

          <br />

          <Typography variant="body2">
            Use the sandbox to develop your ideas and explore our APIs, before
            you progress to a test environment. These APIs are identical to
            production with a limited data set and usage request quota.
          </Typography>

          <br />

          <Typography variant="h6" fontWeight={600} my={0.5}>
            Sandbox Data Limits
          </Typography>

          <br />

          <Typography variant="body2">
            Our sandbox includes property-related data for a limited subset of
            properties. These properties are distributed across 29 suburbs (3
            suburbs per state in Australia and 5 suburbs for New Zealand) with
            about 1,000 properties in each suburb.
          </Typography>

          <br />

          <Typography variant="body2">
            Our sandbox also includes limited market trends data. This data
            includes geographic location types, including suburb, postcode,
            local government area and territory authority (in NZ). This data is
            limited to the past three years for the suburb, postcode, local
            government area and territory authority and one year for state and
            country. This data is sourced from the same 29 suburbs used for
            property-related data.
          </Typography>

          <br />

          <Typography variant="h6" fontWeight={600} my={0.5}>
            Sandbox API Limits
          </Typography>

          <br />

          <Typography variant="body2">
            Our sandbox mirrors the APIs found on our UAT and production
            servers. However, our sandbox does not include the following API
            collections:
          </Typography>

          <ul>
            <li>AVM - origination type</li>
            <li>Geospatial Services</li>
            <li>Property Monitor and Notification</li>
            <li>Property Owner Verification</li>
            <li>Update</li>
          </ul>

          <Typography variant="body2">
            You will be able to access these Services when we promote you to the
            UAT environment.
          </Typography>

          <br />

          <Typography variant="body2">
            For more information on how to integrate our APIs into your
            application, refer to the API documentation.
          </Typography>

          <br />

          <Typography variant="h6" fontWeight={600} my={0.5}>
            Included Sandbox API Services
          </Typography>

          <br />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Application</TableCell>
                  <TableCell>Domain</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Access Authentication Service</TableCell>
                  <TableCell>https://api-sbox.corelogic.asia/access</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Commercial API</TableCell>
                  <TableCell>
                    https://api-sbox.corelogic.asia/commercial-api
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Construction API</TableCell>
                  <TableCell>
                    https://api-sbox.corelogic.asia/construction-api
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell rowSpan={2}>Cordell Sum Sure</TableCell>
                  <TableCell>
                    https://api-sbox.corelogic.asia/sumsure/au
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    https://api-sbox.corelogic.asia/sumsure/nz
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Insurance API</TableCell>
                  <TableCell>
                    https://api-sbox.corelogic.asia/insurance
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Auction API</TableCell>
                  <TableCell>https://api-sbox.corelogic.asia/auction</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Market Trends Charts</TableCell>
                  <TableCell>https://api-sbox.corelogic.asia/charts</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Market Trends Statistics</TableCell>
                  <TableCell>
                    https://api-sbox.corelogic.asia/statistics
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Market Trends Census</TableCell>
                  <TableCell>
                    https://api-sbox.corelogic.asia/census
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Location Search Services</TableCell>
                  <TableCell>
                    https://api-sbox.corelogic.asia/location
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>IntelliVal AVM Services</TableCell>
                  <TableCell>https://api-sbox.corelogic.asia/avm</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>AVM Report Services</TableCell>
                  <TableCell>https://api-sbox.corelogic.asia/avm-report</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Places API</TableCell>
                  <TableCell>https://api-sbox.corelogic.asia/places</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Property Bureau API</TableCell>
                  <TableCell>
                    https://api-sbox.corelogic.asia/propertybureau
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Property Details Services</TableCell>
                  <TableCell>
                    https://api-sbox.corelogic.asia/property-details
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell rowSpan={2}>
                    Property Services (deprecated)
                  </TableCell>
                  <TableCell>
                    https://api-sbox.corelogic.asia/property/au
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    https://api-sbox.corelogic.asia/property/nz
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Property Timeline Services</TableCell>
                  <TableCell>
                    https://api-sbox.corelogic.asia/property-timeline
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Property Profile Report Services</TableCell>
                  <TableCell>
                    https://api-sbox.corelogic.asia/property-profile-report
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Reporting Services</TableCell>
                  <TableCell>
                    https://api-sbox.corelogic.asia/reporting
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Property Risks</TableCell>
                  <TableCell>https://api-sbox.corelogic.asia/risk</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Search Services</TableCell>
                  <TableCell>https://api-sbox.corelogic.asia/search</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>NZ Building Consent Services</TableCell>
                  <TableCell>
                    https://api-sbox.corelogic.asia/building-consent
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>NZ Suggest Title and Owner Services</TableCell>
                  <TableCell>https://api-sbox.corelogic.asia/suggest</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>NZ Title Details Services</TableCell>
                  <TableCell>https://api-sbox.corelogic.asia/titles</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Content Services</TableCell>
                  <TableCell>https://api-sbox.corelogic.asia/content</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>Geospatial Services</TableCell>
                  <TableCell rowSpan={5}>
                    Sandbox environment not applicable for these applications.
                    <br />
                    <br />
                    You will be given access to the UAT environment for
                    integration and testing purposes once you have been
                    on-boarded and authorised.
                    <br />
                    <br />
                    Talk to us if you want to use any of these Services.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Update Services</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Notification Services</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Property Monitor Services</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Verification Services</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <br />
          <br />

          <Typography variant="h6" fontWeight={600} my={0.5}>
            Cotality API Services - UAT and Production
          </Typography>

          <br />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Application</TableCell>
                  <TableCell>Production</TableCell>
                  <TableCell>UAT</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Access Authentication Service</TableCell>
                  <TableCell>https://access.api.cotality.com.au</TableCell>
                  <TableCell>https://access.api-uat.cotality.com.au</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Commercial API</TableCell>
                  <TableCell>https://commercial.api.cotality.com.au</TableCell>
                  <TableCell>
                    https://commercial.api-uat.cotality.com.au
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Construction API</TableCell>
                  <TableCell>
                    https://construction.api.cotality.com.au
                  </TableCell>
                  <TableCell>
                    https://construction.api-uat.cotality.com.au
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell rowSpan={2}>Cordell Sumsure</TableCell>
                  <TableCell>
                    https://cordellsumsure-au.api.cotality.com.au
                  </TableCell>
                  <TableCell>
                    https://cordellsumsure-au.api-uat.cotality.com.au
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    https://cordellsumsure-nz.api.cotality.co.nz
                  </TableCell>
                  <TableCell>
                    https://cordellsumsure-nz.api-uat.cotality.co.nz
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Insurance API</TableCell>
                  <TableCell>https://insurance.api.cotality.com.au</TableCell>
                  <TableCell>https://insurance.api-uat.cotality.com.au</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Content Legal & Disclaimers</TableCell>
                  <TableCell>https://content.api.cotality.com.au</TableCell>
                  <TableCell>https://content.api-uat.cotality.com.au</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>IntelliVal AVM Services</TableCell>
                  <TableCell>https://avm.api.cotality.com.au</TableCell>
                  <TableCell>https://avm.api-uat.cotality.com.au</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>AVM Report Services</TableCell>
                  <TableCell>https://avm-report.api.cotality.com.au</TableCell>
                  <TableCell>https://avm-report.api-uat.cotality.com.au</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Location Search Services</TableCell>
                  <TableCell>https://location.api.cotality.com.au</TableCell>
                  <TableCell>
                    https://location.api-uat.cotality.com.au
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Auction API</TableCell>
                  <TableCell>https://auction.api.cotality.com.au</TableCell>
                  <TableCell>https://auction.api-uat.cotality.com.au</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell rowSpan={2}>
                    Market Trends Charts & Statistics
                  </TableCell>
                  <TableCell>https://charts.api.cotality.com.au</TableCell>
                  <TableCell>https://charts.api-uat.cotality.com.au</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    https://statistics.api.cotality.com.au
                    <br />
                    <br />
                    https://statistics.api.cotality.co.nz
                  </TableCell>
                  <TableCell>
                    https://statistics.api-uat.cotality.com.au
                    <br />
                    <br />
                    https://statistics.api-uat.cotality.co.nz
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Market Trends Census</TableCell>
                  <TableCell>
                    https://census.api.cotality.com.au
                    <br />
                    <br />
                    https://census.api.cotality.co.nz
                  </TableCell>
                  <TableCell>
                    https://census.api-uat.cotality.com.au
                    <br />
                    <br />
                    https://census.api-uat.cotality.co.nz
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Owner Verfication Services</TableCell>
                  <TableCell>
                    https://verification.api.cotality.com.au
                  </TableCell>
                  <TableCell>
                    https://verification.api-uat.cotality.com.au
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Notification Services</TableCell>
                  <TableCell>
                    https://notification.api.cotality.com.au
                  </TableCell>
                  <TableCell>
                    https://notification.api-uat.cotality.com.au
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Property Monitor Services</TableCell>
                  <TableCell>
                    https://property-monitor.api.cotality.com.au
                  </TableCell>
                  <TableCell>
                    https://property-monitor.api-uat.cotality.com.au
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Places Services</TableCell>
                  <TableCell>https://places.api.cotality.com.au</TableCell>
                  <TableCell>https://places.api-uat.cotality.com.au</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Property Bureau API</TableCell>
                  <TableCell>https://propertybureau.api.cotality.com.au</TableCell>
                  <TableCell>https://propertybureau.api-uat.cotality.com.au</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Property Details Services</TableCell>
                  <TableCell>
                    https://property-details.api.cotality.com.au
                    <br />
                    <br />
                    https://property-details.api.cotality.co.nz
                  </TableCell>
                  <TableCell>
                    https://property-details.api-uat.cotality.com.au
                    <br />
                    <br />
                    https://property-details.api-uat.cotality.co.nz
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Property Timeline Services</TableCell>
                  <TableCell>
                    https://property-timeline.api.cotality.com.au
                  </TableCell>
                  <TableCell>
                    https://property-timeline.api-uat.cotality.com.au
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Property Profile Report Services</TableCell>
                  <TableCell>
                    https://property-profile-report.api.cotality.com.au
                  </TableCell>
                  <TableCell>
                    https://property-profile-report.api-uat.cotality.com.au
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Reporting Services</TableCell>
                  <TableCell>
                    https://reporting.api.cotality.com.au
                  </TableCell>
                  <TableCell>
                    https://reporting.api-uat.cotality.com.au
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Property Risk Services</TableCell>
                  <TableCell>https://risk.api.cotality.com.au</TableCell>
                  <TableCell>https://risk.api-uat.cotality.com.au</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Property Services AU (deprecated)</TableCell>
                  <TableCell>https://property-au.api.cotality.com.au</TableCell>
                  <TableCell>https://property-au.api-uat.cotality.com.au</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Property Services NZ (deprecated)</TableCell>
                  <TableCell>https://property-nz.api.cotality.co.nz</TableCell>
                  <TableCell>https://property-nz.api-uat.cotality.co.nz</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Search Services</TableCell>
                  <TableCell>https://search.api.cotality.com.au</TableCell>
                  <TableCell>https://search.api-uat.cotality.com.au</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Update Services</TableCell>
                  <TableCell>https://update.api.cotality.com.au</TableCell>
                  <TableCell>https://update.api-uat.cotality.com.au</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>NZ Building Consent Services</TableCell>
                  <TableCell>
                    https://building-consent.api.cotality.co.nz
                  </TableCell>
                  <TableCell>
                    https://building-consent.api-uat.cotality.co.nz
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>NZ Suggest Title and Owner Services</TableCell>
                  <TableCell>https://suggest.api.cotality.com.au</TableCell>
                  <TableCell>https://suggest.api-uat.cotality.com.au</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>NZ Title Details Services</TableCell>
                  <TableCell>https://titles.api.cotality.co.nz</TableCell>
                  <TableCell>https://titles.api-uat.cotality.co.nz</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </>
  );
};
